/**
 * Cache Manager Utility
 * Provides centralized caching functionality with expiration support
 * 
 * Features:
 * - TTL (Time To Live) support with configurable expiration
 * - Namespace support for different data types
 * - Automatic cleanup of expired items
 * - Memory usage optimization
 * - Event-based cache invalidation
 * 
 * @version 1.0.0
 * @author Zubduq 
 */

import componentLifecycleManager from './componentLifecycleManager.js';

class CacheManager {
  constructor() {
    this.defaultTTL = 5 * 60 * 1000; // 5 minutes default
    this.storagePrefix = 'zubduq_cache_';
    this.eventListeners = new Map();
    this.cleanupInterval = null;
    
    // Register component with lifecycle manager
    componentLifecycleManager.registerComponent('CacheManager', this);
    this.lifecycle = componentLifecycleManager.createMixin('CacheManager');
    
    // Bind methods
    this.get = this.get.bind(this);
    this.set = this.set.bind(this);
    this.invalidate = this.invalidate.bind(this);
    this.clear = this.clear.bind(this);
    
    // Setup periodic cleanup
    this.setupCleanup();
  }

  /**
   * Get cached data by key
   * @param {string} key - Cache key
   * @param {string} namespace - Optional namespace for grouping
   * @returns {any|null} Cached data or null if expired/not found
   */
  get(key, namespace = 'default') {
    try {
      const fullKey = this.buildKey(key, namespace);
      const cached = localStorage.getItem(fullKey);
      
      if (!cached) {
        return null;
      }
      
      const { data, timestamp, ttl } = JSON.parse(cached);
      const now = Date.now();
      
      // Check if cache is expired
      if (now - timestamp > ttl) {
        localStorage.removeItem(fullKey);
        return null;
      }
      
      console.log(`CacheManager: Cache hit for ${namespace}:${key}`);
      return data;
    } catch (error) {
      console.warn('CacheManager: Failed to get cached data:', error);
      return null;
    }
  }

  /**
   * Set cached data with TTL
   * @param {string} key - Cache key
   * @param {any} data - Data to cache
   * @param {string} namespace - Optional namespace for grouping
   * @param {number} ttl - Time to live in milliseconds (optional)
   */
  set(key, data, namespace = 'default', ttl = this.defaultTTL) {
    try {
      const fullKey = this.buildKey(key, namespace);
      const cacheData = {
        data: data,
        timestamp: Date.now(),
        ttl: ttl
      };
      
      localStorage.setItem(fullKey, JSON.stringify(cacheData));
      console.log(`CacheManager: Cached data for ${namespace}:${key} (TTL: ${ttl}ms)`);
      
      // Emit cache set event
      this.emit('cache:set', { key, namespace, data });
    } catch (error) {
      console.warn('CacheManager: Failed to cache data:', error);
    }
  }

  /**
   * Invalidate specific cache entry or entire namespace
   * @param {string} key - Cache key (optional, if not provided clears entire namespace)
   * @param {string} namespace - Namespace to invalidate
   */
  invalidate(key = null, namespace = 'default') {
    try {
      if (key) {
        // Invalidate specific key
        const fullKey = this.buildKey(key, namespace);
        localStorage.removeItem(fullKey);
        console.log(`CacheManager: Invalidated ${namespace}:${key}`);
        this.emit('cache:invalidate', { key, namespace });
      } else {
        // Invalidate entire namespace
        const prefix = this.buildKey('', namespace);
        const keysToRemove = [];
        
        for (let i = 0; i < localStorage.length; i++) {
          const storageKey = localStorage.key(i);
          if (storageKey && storageKey.startsWith(prefix)) {
            keysToRemove.push(storageKey);
          }
        }
        
        keysToRemove.forEach(keyToRemove => localStorage.removeItem(keyToRemove));
        console.log(`CacheManager: Invalidated namespace ${namespace} (${keysToRemove.length} items)`);
        this.emit('cache:invalidate', { namespace });
      }
    } catch (error) {
      console.warn('CacheManager: Failed to invalidate cache:', error);
    }
  }

  /**
   * Clear all cache data
   */
  clear() {
    try {
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`CacheManager: Cleared all cache (${keysToRemove.length} items)`);
      this.emit('cache:clear');
    } catch (error) {
      console.warn('CacheManager: Failed to clear cache:', error);
    }
  }

  /**
   * Get cache statistics
   * @returns {object} Cache statistics
   */
  getStats() {
    const stats = {
      totalItems: 0,
      totalSize: 0,
      namespaces: {},
      expiredItems: 0
    };

    try {
      const now = Date.now();
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            stats.totalItems++;
            stats.totalSize += value.length;
            
            try {
              const { timestamp, ttl } = JSON.parse(value);
              const namespace = this.extractNamespace(key);
              
              if (!stats.namespaces[namespace]) {
                stats.namespaces[namespace] = { items: 0, size: 0 };
              }
              
              stats.namespaces[namespace].items++;
              stats.namespaces[namespace].size += value.length;
              
              // Check if expired
              if (now - timestamp > ttl) {
                stats.expiredItems++;
              }
            } catch (parseError) {
              // Invalid cache entry
            }
          }
        }
      }
    } catch (error) {
      console.warn('CacheManager: Failed to get stats:', error);
    }

    return stats;
  }

  /**
   * Clean up expired cache entries
   */
  cleanup() {
    try {
      const now = Date.now();
      const keysToRemove = [];
      
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(this.storagePrefix)) {
          const value = localStorage.getItem(key);
          if (value) {
            try {
              const { timestamp, ttl } = JSON.parse(value);
              if (now - timestamp > ttl) {
                keysToRemove.push(key);
              }
            } catch (parseError) {
              // Invalid cache entry, remove it
              keysToRemove.push(key);
            }
          }
        }
      }
      
      keysToRemove.forEach(key => localStorage.removeItem(key));
      
      if (keysToRemove.length > 0) {
        console.log(`CacheManager: Cleaned up ${keysToRemove.length} expired items`);
      }
    } catch (error) {
      console.warn('CacheManager: Failed to cleanup cache:', error);
    }
  }

  /**
   * Add event listener for cache events
   * @param {string} event - Event name (cache:set, cache:invalidate, cache:clear)
   * @param {function} callback - Callback function
   */
  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event).push(callback);
  }

  /**
   * Remove event listener
   * @param {string} event - Event name
   * @param {function} callback - Callback function to remove
   */
  off(event, callback) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  /**
   * Emit cache event
   * @param {string} event - Event name
   * @param {any} data - Event data
   */
  emit(event, data) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.warn('CacheManager: Event listener error:', error);
        }
      });
    }
  }

  /**
   * Build full cache key
   * @param {string} key - Cache key
   * @param {string} namespace - Namespace
   * @returns {string} Full cache key
   */
  buildKey(key, namespace) {
    return `${this.storagePrefix}${namespace}:${key}`;
  }

  /**
   * Extract namespace from full cache key
   * @param {string} fullKey - Full cache key
   * @returns {string} Namespace
   */
  extractNamespace(fullKey) {
    const withoutPrefix = fullKey.replace(this.storagePrefix, '');
    const colonIndex = withoutPrefix.indexOf(':');
    return colonIndex > -1 ? withoutPrefix.substring(0, colonIndex) : 'default';
  }

  /**
   * Setup periodic cleanup
   */
  setupCleanup() {
    // Run cleanup every 10 minutes using lifecycle manager
    this.cleanupInterval = this.lifecycle.addInterval(() => {
      this.cleanup();
    }, 10 * 60 * 1000, 'Cache cleanup');
    
    // Also run cleanup on page load
    this.cleanup();
  }

  /**
   * Stop periodic cleanup
   */
  stopCleanup() {
    // Lifecycle manager will handle interval cleanup automatically
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Comprehensive cleanup using lifecycle manager
   */
  destroy() {
    this.lifecycle.lifecycleCleanup();
    this.stopCleanup();
    this.eventListeners.clear();
  }

  /**
   * Check if cache key exists and is valid
   * @param {string} key - Cache key
   * @param {string} namespace - Optional namespace
   * @returns {boolean} True if cache exists and is valid
   */
  has(key, namespace = 'default') {
    return this.get(key, namespace) !== null;
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

// Export for ES6 modules
export default cacheManager;

// Also make available globally
if (typeof window !== 'undefined') {
  window.cacheManager = cacheManager;
}
